import { useMemo } from "react";
import { useSelector } from "react-redux";
import { IRootState } from "../../store/rootState";
import {
  CustomTrail,
  CustomTrailStop,
  TrailContainingStopResult,
} from "../../types/tour";

/**
 * Hook for finding trails and stops
 * Separated from URL sync logic to reduce complexity and improve testability
 */
export const useTrailFinder = () => {
  const { currentTrailId, suggestedTrails } = useSelector(
    (state: IRootState) => state.trails
  );

  // Find current trail - memoized efficiently
  const findCurrentTrail = useMemo((): CustomTrail | null => {
    if (!suggestedTrails || !currentTrailId) return null;

    return (
      suggestedTrails.find((trail) => {
        const trailIdStr = String(trail.trailid || trail.id);
        const currentTrailIdStr = String(currentTrailId);
        return trailIdStr === currentTrailIdStr;
      }) || null
    );
  }, [suggestedTrails, currentTrailId]);

  // Find trail containing a specific stop - using useCallback to prevent recreation
  const findTrailContainingStop = useMemo(
    () => (stopId: number): TrailContainingStopResult | null => {
      if (!suggestedTrails) return null;

      return suggestedTrails.reduce<TrailContainingStopResult | null>(
        (result, trail) => {
          if (result) return result; // Already found

          if (trail?.Stops) {
            const stopIdx = trail.Stops.findIndex((stop: CustomTrailStop) => {
              const stopIdStr = String(stop.stopid || stop.id);
              return stopIdStr === String(stopId);
            });

            if (stopIdx !== -1) {
              return { trail, index: stopIdx };
            }
          }
          return null;
        },
        null
      );
    },
    [suggestedTrails]
  );

  return {
    findCurrentTrail,
    findTrailContainingStop,
    currentTrailId,
    suggestedTrails,
  };
};
